import { __assign, __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useState, forwardRef } from 'react';
import classnames from 'classnames';
import FieldHint from '../FieldHint';
import { HEIGHT, HEIGHT_COMPACT, HEIGHT_SMALL, Root, InputRoot, InputContainer, InputElement, Label, Icon, Adornment, LockSmallFilled, Border, Highlight, SecondaryLabel } from './Input.styles';
var Input = forwardRef(function (_a, ref) {
  var _b = _a.value,
      value = _b === void 0 ? '' : _b,
      message = _a.message,
      label = _a.label,
      secondaryLabel = _a.secondaryLabel,
      _c = _a.error,
      error = _c === void 0 ? false : _c,
      _d = _a.variant,
      variant = _d === void 0 ? 'primary' : _d,
      _e = _a.fullWidth,
      fullWidth = _e === void 0 ? false : _e,
      _f = _a.hideEmptyHint,
      hideEmptyHint = _f === void 0 ? false : _f,
      dense = _a.dense,
      _g = _a.size,
      size = _g === void 0 ? dense ? 'compact' : 'regular' : _g,
      _h = _a.alignRight,
      alignRight = _h === void 0 ? false : _h,
      _j = _a.hintTooltip,
      hintTooltip = _j === void 0 ? size !== 'regular' : _j,
      hintTooltipPlacement = _a.hintTooltipPlacement,
      hintTooltipVariant = _a.hintTooltipVariant,
      adornment = _a.adornment,
      _k = _a.singleLine,
      singleLine = _k === void 0 ? false : _k,
      hideReadOnlyIcon = _a.hideReadOnlyIcon,
      icon = _a.icon,
      className = _a.className,
      highlight = _a.highlight,
      inputProps = __rest(_a, ["value", "message", "label", "secondaryLabel", "error", "variant", "fullWidth", "hideEmptyHint", "dense", "size", "alignRight", "hintTooltip", "hintTooltipPlacement", "hintTooltipVariant", "adornment", "singleLine", "hideReadOnlyIcon", "icon", "className", "highlight"]);

  var readOnly = inputProps.readOnly,
      placeholder = inputProps.placeholder,
      disabled = inputProps.disabled,
      onFocus = inputProps.onFocus,
      onBlur = inputProps.onBlur;

  var _l = useState(false),
      hasFocus = _l[0],
      setHasFocus = _l[1];

  var handleFocus = useCallback(function (e) {
    setHasFocus(true);
    onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
  }, [onFocus]);
  var handleBlur = useCallback(function (e) {
    setHasFocus(false);
    onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
  }, [onBlur]);
  var withReadOnlyIcon = readOnly && !hideReadOnlyIcon && !disabled;
  var withLabel = Boolean(label && !singleLine && size !== 'small');
  var withHighlight = Boolean(highlight && hasFocus && value);
  var rootClassName = classnames(className, size, variant, {
    fullWidth: fullWidth,
    error: error,
    disabled: disabled,
    withLabel: withLabel,
    alignRight: alignRight,
    hasFocus: hasFocus
  });
  return _jsx(Root, __assign({
    className: rootClassName
  }, {
    children: _jsx(FieldHint, __assign({
      variant: hintTooltip ? 'tooltip' : 'static',
      message: message,
      disabled: disabled,
      error: error,
      hideEmpty: hideEmptyHint,
      tooltipPlacement: hintTooltipPlacement,
      tooltipVariant: hintTooltipVariant
    }, {
      children: _jsxs(InputRoot, __assign({
        className: "ui-input__root"
      }, {
        children: [_jsxs(InputContainer, __assign({
          className: "ui-input__container"
        }, {
          children: [_jsx(InputElement, __assign({
            type: "text"
          }, inputProps, {
            className: "ui-input__field",
            placeholder: !withLabel || hasFocus ? placeholder : undefined,
            value: value,
            ref: ref,
            onFocus: handleFocus,
            onBlur: handleBlur
          })), withHighlight && _jsx(Highlight, __assign({
            className: classnames({
              withLabel: withLabel
            }, size)
          }, {
            children: value
          })), withLabel && _jsx(Label, __assign({
            dense: size !== 'regular',
            hasValue: Boolean(value) || hasFocus,
            active: hasFocus,
            error: error,
            disabled: disabled
          }, {
            children: label
          }))]
        })), Boolean(icon || withReadOnlyIcon || secondaryLabel || adornment) && _jsxs(Adornment, __assign({
          className: "ui-input__adornment"
        }, {
          children: [secondaryLabel && _jsx(SecondaryLabel, __assign({
            variant: size === 'regular' ? 'body16' : 'body14',
            weight: "normal",
            color: disabled ? 'disabled' : 'secondary',
            className: "ui-input__secondary-label"
          }, {
            children: secondaryLabel
          })), icon && _jsx(Icon, __assign({
            className: "ui-input__icon"
          }, {
            children: icon
          })), adornment, withReadOnlyIcon && _jsx(LockSmallFilled, {
            className: "ui-input__lock"
          })]
        })), _jsx(Border, {
          className: "ui-input__border"
        })]
      }))
    }))
  }));
});
export { HEIGHT, HEIGHT_COMPACT, HEIGHT_SMALL, HEIGHT_COMPACT as HEIGHT_DENSE };
export default memo(Input);